/**
 * PalCare roles (Palcare Admin, role id 1, weight 1000; Palcare, role id 2, weight 100)
 * may only be given to users on a PalCare community, one whose corporate_id is 1.
 * Decided 2026-10-29. Every V2 screen that picks a role filters through here: the Users
 * drawer, the user profile drawer, EHR add-user approval and the CSV upload.
 *
 * The user-manage Lambdas (User_Manager.py, Portal_User_Manager.py) enforce the same
 * rule, so this only decides what the dropdowns offer.
 *
 * The community's corporate_id comes from the session's campusData (built from
 * sp_get_campus_EHR). A session without it is treated as not PalCare, so the roles are
 * hidden rather than offered on the wrong community.
 */
import { getCampusConfig } from '../services/session.js';

export const PALCARE_CORPORATE_ID = 1;
const PALCARE_ROLE_IDS = [1, 2];
/** Weight above which a role is a PalCare role (Vendor is 90, Palcare is 100). */
const PALCARE_ROLE_WEIGHT_FLOOR = 99;

/** True for role ids 1 and 2, or any role weighted above 99. */
export function isPalcareRole(role) {
  if (!role) return false;
  return PALCARE_ROLE_IDS.includes(parseInt(role.user_role_id, 10))
    || parseInt(role.user_role_weight || 0, 10) > PALCARE_ROLE_WEIGHT_FLOOR;
}

/** True when the community belongs to PalCare (corporate_id 1). Default: the current campus. */
export function isPalcareCampus(campusId) {
  const corp = getCampusConfig(campusId)?.corporate_id;
  return corp != null && parseInt(corp, 10) === PALCARE_CORPORATE_ID;
}

/**
 * Drop the PalCare roles unless the community is PalCare.
 *
 * `keepRoleId` is the role the user being edited already has. It stays in the list so the
 * dropdown can still show it for a PalCare user who is already on another community; the
 * backend lets that user be edited with the role unchanged, but not given it anew.
 */
export function rolesForCampus(roles, campusId, keepRoleId) {
  const list = roles || [];
  if (isPalcareCampus(campusId)) return list;
  const keep = keepRoleId != null && keepRoleId !== '' ? String(keepRoleId) : null;
  return list.filter((r) => !isPalcareRole(r) || (keep && String(r.user_role_id) === keep));
}
